import {
    Box,
    Container,
    Grid,
    Card,
    CardContent,
    CardHeader,
    Typography,
    Chip,
    Stack,
    Divider,
} from "@mui/material";
import DiscDiamondWithBase from "./DiscDiamondWithBase";
import { ReactComponent as DiscSvg } from "./DISC.svg";
import { useTranslation } from 'react-i18next';

const getSmartGridSize = (index, total) => {
    if (index === total - 1 && total % 2 === 1) {
        return { xs: 12, md: 12 };
    }
    return { xs: 12, md: 6 };
};

// ---------- helpers ----------
const TRAITS = ["D", "I", "S", "C"];
const COLORS = { D: "#ff0000", I: "#ffd600", S: "#006300", C: "#0001fc" };

// Helper para determinar el color de texto según el rasgo
const getTextColor = (trait) => {
    return trait === "I" ? "#1a1a1a" : "#ffffff";
};

// Función para obtener las etiquetas de estilo traducidas
const getStyleLabel = (t) => ({
    D: t('disc.dominance'),
    I: t('disc.influence'),
    S: t('disc.steadiness'),
    C: t('disc.conscientiousness')
});

function fsTsToDate(ts) {
    if (!ts || typeof ts._seconds !== "number") return new Date();
    const ms = ts._seconds * 1000 + Math.round((ts._nanoseconds || 0) / 1e6);
    return new Date(ms);
}

function toResponsesArray(responsesObj) {
    if (!responsesObj) return [];
    const arr = [];
    Object.keys(responsesObj).forEach((k) => {
        const m = k.match(/^page(\d+)$/i);
        if (!m) return;
        const page = Number(m[1]);
        const ans = responsesObj[k]?.answers || {};
        if (typeof ans.mostIndex === "number" && typeof ans.leastIndex === "number") {
            arr.push({ page, mostIndex: ans.mostIndex, leastIndex: ans.leastIndex });
        }
    });
    return arr.sort((a, b) => a.page - b.page);
}

function computeReliabilityFromResponses(responsesObj, totalPages = 24) {
    const arr = toResponsesArray(responsesObj);
    const answered = arr.length;
    const completeness = Math.min(1, answered / (totalPages || 24));
    const pos = { most: [0, 0, 0, 0], least: [0, 0, 0, 0] };
    for (const r of arr) {
        if (r.mostIndex >= 0) pos.most[r.mostIndex] += 1;
        if (r.leastIndex >= 0) pos.least[r.leastIndex] += 1;
    }
    const maxMost = Math.max(...pos.most);
    const maxLeast = Math.max(...pos.least);
    const n = arr.length || 1;
    const bias = Math.max(maxMost, maxLeast) / n;
    const biasPenalty = Math.max(0, (bias - 0.4) / 0.6);
    const reliability = Math.round(100 * (completeness * (1 - 0.4 * biasPenalty)));
    return Math.max(0, Math.min(100, reliability));
}

function orderedNatural(scores) {
    return TRAITS.map((t) => ({ t, v: scores?.[t] || 0 })).sort((a, b) => b.v - a.v);
}

function getValidityChipConfig(validityScore, t) {
    if (validityScore >= 85) {
        return {
            label: t('disc.highReliability'),
            color: 'success',
            severity: 'alta'
        };
    } else if (validityScore >= 75) {
        return {
            label: t('disc.acceptableReliability'),
            color: 'success',
            severity: 'aceptable'
        };
    } else if (validityScore >= 50) {
        return {
            label: t('disc.questionableReliability'),
            color: 'warning',
            severity: 'cuestionable'
        };
    } else if (validityScore >= 25) {
        return {
            label: t('disc.lowReliability'),
            color: 'error',
            severity: 'baja'
        };
    } else {
        return {
            label: t('disc.compromisedValidity'),
            color: 'error',
            severity: 'muy_baja'
        };
    }
}

// Barras minimalistas
function Bars({ scores }) {
    const maxH = 120;
    return (
        <Stack direction="row" justifyContent="space-around" alignItems="end" sx={{ height: maxH + 60 }}>
            {TRAITS.map((t) => {
                const v = scores?.[t] || 0;
                return (
                    <Box key={t} textAlign="center">
                        <Box
                            sx={{
                                width: 32,
                                height: `${(v / 100) * maxH}px`,
                                bgcolor: COLORS[t],
                                borderRadius: 1,
                            }}
                            title={`${t}: ${v}%`}
                        />
                        <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                            {t}
                        </Typography>
                        <Typography variant="caption" sx={{ display: "block", fontSize: '0.7rem', color: 'text.secondary' }}>
                            {v}%
                        </Typography>
                    </Box>
                );
            })}
        </Stack>
    );
}

// Tarjeta lista simple
function ListCard({ title, items }) {
    return (
        <Card variant="outlined">
            <CardHeader title={title} />
            <CardContent>
                <Stack spacing={1}>
                    {items && items.length ? (
                        items.map((it, idx) => (
                            <Typography key={idx} variant="body2">• {it}</Typography>
                        ))
                    ) : (
                        <Typography variant="body2" color="text.secondary">Sin contenido.</Typography>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}

function StatusPill({ delta }) {
    const sign = delta > 0 ? "+" : "";
    return <Chip
        size="small"
        label={`${sign}${delta}%`}
        sx={{
            fontWeight: 600,
            bgcolor: "white",
            color: "#000",
            border: "1px solid white"
        }}
    />;
}

// Narrativa generada para "Análisis Emocional General"
function buildEmotionalOverview({ natural, adapted, deltas, shape, STYLE_LABEL }) {
    const pos = TRAITS.filter((t) => (deltas?.[t] || 0) > 0);
    const neg = TRAITS.filter((t) => (deltas?.[t] || 0) < 0);
    const trend =
        pos.length && neg.length
            ? "un patrón mixto de adaptación, intensificando algunos rasgos mientras suprime otros"
            : pos.length
                ? "una tendencia a intensificar rasgos en el perfil adaptado"
                : neg.length
                    ? "una tendencia a moderar rasgos en el perfil adaptado"
                    : "estabilidad entre natural y adaptado";

    const parts = [];
    parts.push(
        `Esta persona presenta un perfil con rasgos dominantes en ${STYLE_LABEL[orderedNatural(natural)[0].t]}, lo que define su estilo conductual principal en el ámbito laboral.`
    );
    parts.push(`Se observa ${trend}.`);
    if (pos.length) {
        const list = pos.map((t) => STYLE_LABEL[t].toLowerCase()).join(", ");
        parts.push(`Se intensifica(n) ${list} en el perfil adaptado.`);
    }
    if (neg.length) {
        const list = neg.map((t) => STYLE_LABEL[t].toLowerCase()).join(", ");
        parts.push(`Se modera(n) ${list} en el perfil adaptado.`);
    }
    if (shape?.compression && shape.compression !== "none") {
        parts.push(
            shape.compression === "both"
                ? "Se aprecia compresión en ambos perfiles, con menor diferenciación de rasgos."
                : shape.compression === "natural"
                    ? "Se aprecia compresión en el perfil natural, con menor diferenciación interna."
                    : "Se aprecia compresión en el perfil adaptado, con expresión más contenida por el entorno."
        );
    }
    parts.push(
        "Es importante considerar estos factores de adaptación al evaluar el desempeño, asignar responsabilidades y diseñar planes de desarrollo profesional."
    );
    return parts.join(" ");
}

// ---------- componente principal (página) ----------
export default function DiscReportMuiFromData({ data }) {
    const { t } = useTranslation();

    const STYLE_LABEL = getStyleLabel(t);

    const dr = data?.discResults || {};
    const fs = dr?.finalScores || {};
    const report = fs?.report || null;

    const natural = report?.charts?.natural || fs?.profile?.natural || { D: 0, I: 0, S: 0, C: 0 };
    const adapted = report?.charts?.adapted || fs?.profile?.adapted || { D: 0, I: 0, S: 0, C: 0 };

    const usage = report?.usage;

    const deltas = report?.naturalVsAdapted?.deltas || {
        D: (adapted.D || 0) - (natural.D || 0),
        I: (adapted.I || 0) - (natural.I || 0),
        S: (adapted.S || 0) - (natural.S || 0),
        C: (adapted.C || 0) - (natural.C || 0),
    };

    const shape = report.shape;

    const narratives = report?.narratives || {
        strengths: [],
        developmentAreas: [],
        motivators: [],
        demotivators: [],
        teamRole: "",
        avoids: [],
    };

    const orderedNat = orderedNatural(natural);
    const reliability = fs?.validityIndex?.validityScore || computeReliabilityFromResponses(dr?.responses, 24);
    const completed = fsTsToDate(data?.completedAt || dr?.completedAt || data?.modified);

    const emotionalOverview = buildEmotionalOverview({
        natural,
        adapted,
        deltas,
        shape,
        STYLE_LABEL
    });

    const teamRoleText = narratives.teamRole;

    const traitDescriptions = report?.traitDescriptions;

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Caja de confiabilidad */}
            <Card sx={{ mb: 3, borderColor: "#c7ecd3", bgcolor: "#e9f7ef" }} variant="outlined">
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={3}>
                            <Typography variant="subtitle1" fontWeight={700}>
                                {t('disc.reliabilityAnalysis')}
                            </Typography>
                            <Typography variant="h4" fontWeight={800}>
                                {reliability}%
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                                {t('disc.completed')}
                            </Typography>
                            <Typography variant="subtitle1">
                                {completed.toLocaleDateString()}{" "}
                                {completed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                                {t('disc.state')}
                            </Typography>
                            <Chip
                                label={getValidityChipConfig(reliability, t).label}
                                color={getValidityChipConfig(reliability, t).color}
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                                {t('disc.primaryStyle')}
                            </Typography>
                            <Chip
                                label={`${STYLE_LABEL[orderedNat[0]?.t || "D"]} (${orderedNat[0]?.t || "D"})`}
                                sx={{
                                    bgcolor: COLORS[orderedNat[0]?.t || "D"],
                                    color: getTextColor(orderedNat[0]?.t || "D"),
                                    fontWeight: 600
                                }}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Gráficos principales */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                        <CardHeader title={t('disc.naturalProfile')} />
                        <CardContent>
                            <Bars scores={natural} />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                        <CardHeader title={t('disc.adaptedProfile')} />
                        <CardContent>
                            <Bars scores={adapted} />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Zona de Flexibilidad */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                    <Card variant="outlined">
                        <CardHeader title={t('disc.flexibilityZone')} />
                        <CardContent>
                            <DiscDiamondWithBase
                                BaseSvg={DiscSvg}
                                natural={natural}
                                adapted={adapted}
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Características / Fortalezas */}
            <Grid container spacing={2} sx={{ mt: 1 }}>
                {(data?.aiAnalysis?.caracteristicas || narratives.characteristics)?.length > 0 && (
                    <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                        <ListCard
                            title={t('disc.characteristics')}
                            items={data?.aiAnalysis?.caracteristicas || narratives.characteristics}
                            t={t}
                            sx={{ height: '100%', width: '100%' }}
                        />
                    </Grid>
                )}
                {(data?.aiAnalysis?.fortalezas || narratives.strengths)?.length > 0 && (
                    <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                        <ListCard
                            title={t('disc.strengths')}
                            items={data?.aiAnalysis?.fortalezas || narratives.strengths}
                            t={t}
                            sx={{ height: '100%', width: '100%' }}
                        />
                    </Grid>
                )}
            </Grid>

            {/* Motivadores / Desmotivadores */}
            <Grid container spacing={2} sx={{ mt: 1 }}>
                {(data?.aiAnalysis?.motivadores || narratives.motivators)?.length > 0 && (
                    <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                        <ListCard
                            title={t('disc.motivators')}
                            items={data?.aiAnalysis?.motivadores || narratives.motivators}
                            t={t}
                            sx={{ height: '100%', width: '100%' }}
                        />
                    </Grid>
                )}
                {(data?.aiAnalysis?.desmotivadores || narratives.demotivators)?.length > 0 && (
                    <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                        <ListCard
                            title={t('disc.demotivators')}
                            items={data?.aiAnalysis?.desmotivadores || narratives.demotivators}
                            t={t}
                            sx={{ height: '100%', width: '100%' }}
                        />
                    </Grid>
                )}
            </Grid>

            {/* Áreas de Desarrollo / Evita */}
            <Grid container spacing={2} sx={{ mt: 1 }}>
                {(data?.aiAnalysis?.areasDesarrollo || narratives.developmentAreas)?.length > 0 && (
                    <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                        <ListCard
                            title={t('disc.developmentAreas')}
                            items={data?.aiAnalysis?.areasDesarrollo || narratives.developmentAreas}
                            t={t}
                            sx={{ height: '100%', width: '100%' }}
                        />
                    </Grid>
                )}
                {(data?.aiAnalysis?.cosasQueEvita || narratives.avoids)?.length > 0 && (
                    <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                        <ListCard
                            title={t('disc.thingsThatTryToAvoid')}
                            items={data?.aiAnalysis?.cosasQueEvita || narratives.avoids}
                            t={t}
                            sx={{ height: '100%', width: '100%' }}
                        />
                    </Grid>
                )}
            </Grid>

            {/* Análisis de Sobreuso y Subuso */}
            <Card sx={{ mb: 3, mt: 3 }}>
                <CardHeader title={t('disc.overuseUnderuseAnalysis')} sx={{ bgcolor: "#f8fafc" }} />
                <CardContent>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        {report.narratives.overuseUnderuse.narrative}
                    </Typography>

                    {data?.aiAnalysis?.analisisSobreusoSubuso?.explicacionGeneral && (
                        <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #e2e8f0" }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                                {t('disc.detailedAnalysis')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                {data.aiAnalysis.analisisSobreusoSubuso.explicacionGeneral}
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Rol en el Equipo */}
            <Card sx={{ mb: 3 }}>
                <CardHeader title={t('disc.teamRole')} sx={{ bgcolor: "#f8fafc" }} />
                <CardContent>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        {teamRoleText}
                    </Typography>

                    {data?.aiAnalysis?.rolEnEquipo?.ejemplosPracticos && (
                        <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #e2e8f0" }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                                {t('disc.practicalExamples')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                {data.aiAnalysis.rolEnEquipo.ejemplosPracticos}
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Interpretación Emocional y Proporcionalidad */}
            <Card sx={{ mb: 3 }}>
                <CardHeader title={t('disc.emotionalInterpretationTitle')} sx={{ bgcolor: "#f8fafc" }} />
                <CardContent>
                    <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                        {t('disc.generalEmotionalAnalysis')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        {report.emotionalOverview}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                        {t('disc.dominantTraits')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {t('disc.dominantTraitsDescription')}
                    </Typography>
                    {orderedNat.slice(0, 2).map((item) => (
                        <Typography key={item.t} variant="body2" color="text.secondary">
                            <b>{item.t} {item.v}%</b> {traitDescriptions[item.t]}
                        </Typography>
                    ))}

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                        {t('disc.nonDominantTraits')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {t('disc.nonDominantTraitsDescription')}
                    </Typography>
                    {orderedNat.slice(2).map((item) => (
                        <Typography key={item.t} variant="body2" color="text.secondary">
                            <b>{item.t} {item.v}%</b> {traitDescriptions[item.t]}
                        </Typography>
                    ))}

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                        {t('disc.adaptationAnalysis')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        {t('disc.adaptationPatternDescription')}
                    </Typography>

                    <Grid container spacing={2}>
                        {TRAITS.map((tr) => {
                            const delta = deltas[tr] || 0;
                            const change = report?.naturalVsAdapted?.changes?.find((c) => c.trait === tr);
                            const bgColor = COLORS[tr];

                            const gptChange = data?.aiAnalysis?.naturalVsAdaptado?.cambiosPorRasgo?.find((c) => c.rasgo === tr);

                            return (
                                <Grid item xs={12} md={6} key={tr} sx={{ display: 'flex' }}>
                                    <Box sx={{
                                        p: 2,
                                        border: `2px solid ${COLORS[tr]}`,
                                        borderRadius: 2,
                                        bgcolor: bgColor,
                                        height: '100%',
                                        width: '100%',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}>
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                            <Box sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: "50%",
                                                bgcolor: "white",
                                                border: `2px solid white`,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: COLORS[tr],
                                                fontWeight: 700
                                            }}>
                                                {tr}
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="subtitle2" fontWeight={600} sx={{ color: getTextColor(tr) }}>
                                                    {t('disc.natural')} {natural[tr]}% → {t('disc.adapted')} {adapted[tr]}%
                                                </Typography>
                                            </Box>
                                            <StatusPill delta={delta} />
                                        </Stack>
                                        <Typography variant="caption" sx={{ color: getTextColor(tr) }}>
                                            {change?.interpretation || t('disc.noInformation')}
                                        </Typography>

                                        {gptChange?.interpretacion && (
                                            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${tr === 'I' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.3)'}` }}>
                                                <Typography variant="body2" sx={{ lineHeight: 1.6, color: getTextColor(tr) }}>
                                                    {gptChange.interpretacion}
                                                </Typography>
                                            </Box>
                                        )}

                                        {gptChange?.significadoPractico && (
                                            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${tr === 'I' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.3)'}` }}>
                                                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: getTextColor(tr) }}>
                                                    {t('disc.practicalExample')}
                                                </Typography>
                                                <Typography variant="body2" sx={{ lineHeight: 1.6, color: getTextColor(tr) }}>
                                                    {gptChange.significadoPractico}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                </CardContent>
            </Card>

            {/* Afinidad con el Cargo */}
            {data?.aiAnalysis?.afinidadConCargo && (
                <Card sx={{ mb: 3 }}>
                    <CardHeader
                        title={t('disc.positionAffinity')}
                        sx={{ bgcolor: "#f8fafc" }}
                    />
                    <CardContent>
                        <Grid container spacing={3} alignItems="center">
                            {/* Porcentaje destacado */}
                            <Grid item xs={12} md={4}>
                                <Box sx={{ textAlign: "center", p: 3, bgcolor: "#f8fafc", borderRadius: 2 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                                        {t('disc.affinityPercentage')}
                                    </Typography>
                                    <Typography
                                        variant="h2"
                                        fontWeight={800}
                                        sx={{
                                            color: data.aiAnalysis.afinidadConCargo.porcentajeAfinidad >= 75
                                                ? "#059669"
                                                : data.aiAnalysis.afinidadConCargo.porcentajeAfinidad >= 50
                                                    ? "#d97706"
                                                    : "#dc2626"
                                        }}
                                    >
                                        {data.aiAnalysis.afinidadConCargo.porcentajeAfinidad}%
                                    </Typography>
                                    <Chip
                                        label={
                                            data.aiAnalysis.afinidadConCargo.porcentajeAfinidad >= 75
                                                ? t('disc.highAffinity')
                                                : data.aiAnalysis.afinidadConCargo.porcentajeAfinidad >= 50
                                                    ? t('disc.moderateAffinity')
                                                    : t('disc.lowAffinity')
                                        }
                                        color={
                                            data.aiAnalysis.afinidadConCargo.porcentajeAfinidad >= 75
                                                ? "success"
                                                : data.aiAnalysis.afinidadConCargo.porcentajeAfinidad >= 50
                                                    ? "warning"
                                                    : "error"
                                        }
                                        sx={{ mt: 2 }}
                                    />
                                </Box>
                            </Grid>

                            {/* Explicación */}
                            <Grid item xs={12} md={8}>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                                    {t('disc.affinityAnalysis')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                    {data.aiAnalysis.afinidadConCargo.explicacion}
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* Afinidad con Competencias */}
            {data?.aiAnalysis?.afinidadConCompetencias && data.aiAnalysis.afinidadConCompetencias.length > 0 && (
                <Card sx={{ mb: 3 }}>
                    <CardHeader
                        title={t('disc.competenciesAffinity')}
                        sx={{ bgcolor: "#f8fafc" }}
                    />
                    <CardContent>
                        <Grid container spacing={2}>
                            {data.aiAnalysis.afinidadConCompetencias.map((comp, index) => {
                                const porcentaje = comp.porcentajeAfinidad;
                                const bgColor = porcentaje >= 75 ? '#d4edda' : porcentaje >= 50 ? '#fff3cd' : '#f8d7da';
                                const borderColor = porcentaje >= 75 ? '#10b981' : porcentaje >= 50 ? '#f59e0b' : '#ef4444';
                                const chipBg = porcentaje >= 75 ? '#10b981' : porcentaje >= 50 ? '#f59e0b' : '#ef4444';

                                return (
                                    <Grid
                                        key={index}
                                        item
                                        {...getSmartGridSize(index, data.aiAnalysis.afinidadConCompetencias.length)}
                                    >
                                        <Box
                                            sx={{
                                                p: 2,
                                                bgcolor: bgColor,
                                                borderRadius: 1,
                                                border: `2px solid ${borderColor}`,
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column'
                                            }}
                                        >
                                            {/* Header con nombre y porcentaje */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                                <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
                                                    {comp.competencia}
                                                </Typography>
                                                <Chip
                                                    label={`${porcentaje}%`}
                                                    sx={{
                                                        bgcolor: chipBg,
                                                        color: 'white',
                                                        fontWeight: 700,
                                                        fontSize: '0.75rem'
                                                    }}
                                                    size="small"
                                                />
                                            </Box>

                                            {/* Explicación */}
                                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, flex: 1 }}>
                                                {comp.explicacion}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* Plan de Desarrollo 90 Días (GPT) */}
            {data?.aiAnalysis?.planDesarrollo90Dias && (
                <Card sx={{ mb: 3 }}>
                    <CardHeader
                        title={t('disc.developmentPlan90Days')}
                        sx={{ bgcolor: "#f8fafc" }}
                    />
                    <CardContent>
                        {/* Fortalezas Clave */}
                        {data.aiAnalysis.planDesarrollo90Dias.fortalezasClave && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "#10b981", mb: 2 }}>
                                    {data.aiAnalysis.planDesarrollo90Dias.fortalezasClave.titulo}
                                </Typography>
                                <Stack spacing={1.5} sx={{ mb: 2 }}>
                                    {data.aiAnalysis.planDesarrollo90Dias.fortalezasClave.fortalezas.map((fortaleza, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{
                                                p: 2,
                                                bgcolor: "#d4edda",
                                                borderLeft: "4px solid #10b981",
                                                borderRadius: 1
                                            }}
                                        >
                                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                                ✓ {fortaleza}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                                {data.aiAnalysis.planDesarrollo90Dias.fortalezasClave.comoAprovecharlas && (
                                    <Box
                                        sx={{
                                            p: 2,
                                            bgcolor: "#f0f9ff",
                                            borderRadius: 2,
                                            border: "1px solid #3b82f6"
                                        }}
                                    >
                                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: "#3b82f6" }}>
                                            {t('disc.howToLeverageThem')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                            {data.aiAnalysis.planDesarrollo90Dias.fortalezasClave.comoAprovecharlas}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}

                        <Divider sx={{ my: 3 }} />

                        {/* Áreas de Mejora con Tips Concretos */}
                        {data.aiAnalysis.planDesarrollo90Dias.areasMejora && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "#f59e0b", mb: 2 }}>
                                    {data.aiAnalysis.planDesarrollo90Dias.areasMejora.titulo}
                                </Typography>
                                <Stack spacing={2}>
                                    {data.aiAnalysis.planDesarrollo90Dias.areasMejora.areas.map((item, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{
                                                p: 2.5,
                                                bgcolor: "#fff3cd",
                                                borderRadius: 2,
                                                border: "1px solid #f59e0b"
                                            }}
                                        >
                                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700, color: "#d97706" }}>
                                                {item.area}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    bgcolor: "white",
                                                    borderRadius: 1,
                                                    my: 1.5,
                                                    borderLeft: "3px solid #f59e0b"
                                                }}
                                            >
                                                <Typography variant="caption" sx={{ fontWeight: 600, color: "#d97706", display: "block", mb: 0.5 }}>
                                                    {t('disc.concreteTip')}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                                    {item.tipConcreto}
                                                </Typography>
                                            </Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic", display: "block" }}>
                                                <strong>{t('disc.whyItMatters')}</strong> {item.porqueImporta}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        <Divider sx={{ my: 3 }} />

                        {/* Estrategias para Potenciar Rol Actual */}
                        {data.aiAnalysis.planDesarrollo90Dias.estrategiasRolActual && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "#3b82f6", mb: 2 }}>
                                    {data.aiAnalysis.planDesarrollo90Dias.estrategiasRolActual.titulo}
                                </Typography>
                                <Stack spacing={1.5}>
                                    {data.aiAnalysis.planDesarrollo90Dias.estrategiasRolActual.estrategias.map((estrategia, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{
                                                p: 2,
                                                bgcolor: "#dbeafe",
                                                borderLeft: "4px solid #3b82f6",
                                                borderRadius: 1
                                            }}
                                        >
                                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                                {idx + 1}. {estrategia}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        <Divider sx={{ my: 3 }} />

                        {/* Mini Checklists Accionables */}
                        {data.aiAnalysis.planDesarrollo90Dias.miniChecklists && (
                            <Box>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "#6366f1", mb: 2 }}>
                                    {data.aiAnalysis.planDesarrollo90Dias.miniChecklists.titulo}
                                </Typography>

                                <Grid container spacing={2}>
                                    {/* Checklist Semanal */}
                                    {data.aiAnalysis.planDesarrollo90Dias.miniChecklists.checklistSemanal && (
                                        <Grid item xs={12} md={4}>
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    bgcolor: "#f0fdf4",
                                                    borderRadius: 2,
                                                    border: "1px solid #10b981",
                                                    height: "100%"
                                                }}
                                            >
                                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700, color: "#059669" }}>
                                                    {data.aiAnalysis.planDesarrollo90Dias.miniChecklists.checklistSemanal.nombre}
                                                </Typography>
                                                <Stack spacing={1} sx={{ mt: 1.5 }}>
                                                    {data.aiAnalysis.planDesarrollo90Dias.miniChecklists.checklistSemanal.items.map((item, idx) => (
                                                        <Typography key={idx} variant="body2" color="text.secondary">
                                                            □ {item}
                                                        </Typography>
                                                    ))}
                                                </Stack>
                                            </Box>
                                        </Grid>
                                    )}

                                    {/* Checklist Mensual */}
                                    {data.aiAnalysis.planDesarrollo90Dias.miniChecklists.checklistMensual && (
                                        <Grid item xs={12} md={4}>
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    bgcolor: "#fef3c7",
                                                    borderRadius: 2,
                                                    border: "1px solid #f59e0b",
                                                    height: "100%"
                                                }}
                                            >
                                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700, color: "#d97706" }}>
                                                    {data.aiAnalysis.planDesarrollo90Dias.miniChecklists.checklistMensual.nombre}
                                                </Typography>
                                                <Stack spacing={1} sx={{ mt: 1.5 }}>
                                                    {data.aiAnalysis.planDesarrollo90Dias.miniChecklists.checklistMensual.items.map((item, idx) => (
                                                        <Typography key={idx} variant="body2" color="text.secondary">
                                                            □ {item}
                                                        </Typography>
                                                    ))}
                                                </Stack>
                                            </Box>
                                        </Grid>
                                    )}

                                    {/* Checklist para Situaciones Clave */}
                                    {data.aiAnalysis.planDesarrollo90Dias.miniChecklists.checklistAntesSituacionesClave && (
                                        <Grid item xs={12} md={4}>
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    bgcolor: "#e0e7ff",
                                                    borderRadius: 2,
                                                    border: "1px solid #6366f1",
                                                    height: "100%"
                                                }}
                                            >
                                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700, color: "#4f46e5" }}>
                                                    {data.aiAnalysis.planDesarrollo90Dias.miniChecklists.checklistAntesSituacionesClave.nombre}
                                                </Typography>
                                                <Stack spacing={2} sx={{ mt: 1.5 }}>
                                                    {data.aiAnalysis.planDesarrollo90Dias.miniChecklists.checklistAntesSituacionesClave.situaciones.map((situacion, idx) => (
                                                        <Box key={idx}>
                                                            <Typography variant="caption" sx={{ fontWeight: 600, color: "#4f46e5", display: "block", mb: 0.5 }}>
                                                                {situacion.situacion}:
                                                            </Typography>
                                                            {situacion.checks.map((check, checkIdx) => (
                                                                <Typography key={checkIdx} variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                                                                    □ {check}
                                                                </Typography>
                                                            ))}
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            </Box>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            )}

        </Container>
    );
}